<template src="./template.html"></template>
<style lang="scss" src="./styles.scss" scoped></style>

<script>
import { mapActions, mapState } from 'vuex'
import FallbackAvatar from '@/components/ui/FallbackAvatar'
import LanguageSwitcher from '@/components/ui/LanguageChanger'
import NavigationItemHorizontal from '@/components/navigation/NavigationItemHorizontal'
import ConfirmationOverlay from '@/components/overlays/ConfirmationOverlay'
import api from '@/api'

export default {
  name: 'AppNavigation',
  components: {
    FallbackAvatar,
    LanguageSwitcher,
    NavigationItemHorizontal,
    ConfirmationOverlay,
  },
  props: {
    activityBgColor: {
      type: String,
      default: 'accent',
    },
    appendSearch: Boolean,
    navigationBgColor: {
      type: String,
      default: 'main-darken-5',
    },
    prependSearch: Boolean,
    routes: Array,
    settingsRoutes: Array,
    showLanguageSwitcher: Boolean,
    user: Object,
    value: Boolean,
    right: {
      type: Boolean,
      default: true,
    },
    advancedSearchType: String,
  },
  data() {
    return {
      open: false,
      search: null,
      isSettingsMenu: false,
      getConsent: false,
      toggleActionUrl: null,
      pages: {},
    }
  },
  computed: {
    ...mapState('activities', [ 'unread_activities' ]),
    ...mapState('auth', [
      'location',
      'settings',
      'consent_accepted',
    ]),
    ...mapState('pages', [ 'legal_pages' ]),
    links() {
      const links = []
      if (process.env.NODE_ENV === 'development') {
        links.push({ title: 'Dummy', icon: this.iconMapping('dummy'), type: 'dummy' })
      }
      return links
    },
    getLogo() {
      const logo =
        this.settings &&
        this.settings.base_options &&
        this.settings.base_options.show_company_logo &&
        this.location &&
        this.location.image
          ? this.location.image
          : this.customerConfig.images.logo
      return logo
    },
  },
  watch: {
    value: {
      handler() {
        this.open = this.value
        this.isSettingsMenu = false
      },
      immediate: true,
    },
    open() {
      this.$emit('input', this.open)
    },
    '$route'(to, from) {
      this.toggleBodyClass('horizontal-navigation', true)
    },
  },
  created() {
    if (this.user) {
      this.getCountUnreadActivities()
    }
  },
  mounted() {
    this.getLegalPages().then(_ => {
      this.setPages()
    })
    this.toggleBodyClass('horizontal-navigation', true)
    window.resizeTimeout = null
    window.initResize = this.initResize
    window.initResize(true)
    window.addEventListener('resize', window.initResize)
  },
  methods: {
    ...mapActions('activities', [ 'getCountUnreadActivities' ]),
    ...mapActions('auth', [ 'getSettingsMenu' ]),
    ...mapActions('pages', [ 'getLegalPages' ]),
    onSearch() {
      if (!this.search) return false

      if (this.$route.name === 'search-results' && this.$route.query.q !== this.search) {
        this.$router.push({ query: { q: this.search } })
      } else if (this.$route.query.q !== this.search) {
        this.$router.push({ name: 'search-results', query: { q: this.search } })
      }

      this.search = null
    },
    toggleAction(url, status, force = false) {
      this.toggleActionUrl = url
      if (!status && !this.consent_accepted && !force) {
        this.getConsent = true
      } else {
        this.closeOverlay()
        api.items.editItem(url).then(_ => {
          this.getSettingsMenu()
        })
      }
    },
    getConsentOverlayObject(pages) {
      return {
        title: this.$t('login.toggle_consent.title'),
        text: this.$t('login.toggle_consent.text', pages),
        button_text: this.$t('login.toggle_consent.button_text'),
        checkbox: true,
      }
    },
    toggleActionConfirm() {
      api.items.editItem('auth.accept_consent').then(_ => {
        this.toggleAction(this.toggleActionUrl, false, true)
        this.getSettingsMenu()
      })
    },
    closeOverlay() {
      this.getConsent = false
      this.toggleActionUrl = null
    },
    setPages() {
      Object.entries(this.legal_pages).forEach(page => {
        this.pages[page[0]] = this.$router.resolve({ name: 'page', params: { id: page[1].page_id } }).href || ''
      })
    },
    initResize(immediate = false, triggerScroll = true) {
      window.resizeTimeout = window.resizeTimeout || null
      clearTimeout(window.resizeTimeout)
      window.resizeTimeout = setTimeout(function() {
        document.querySelectorAll('.container>.menu-row').forEach(elem => {
          const container = elem.parentNode
          const header = document.querySelector('#appBar')
          const menu = header.childNodes[1]
          const isScrolled = Boolean(menu.classList.contains('position-on-scroll'))
          const imageWidth = document.querySelector('#logo-image .v-toolbar__title').offsetWidth
          let searchProfileWidth = 0
          document.querySelectorAll('#search-profile-block button').forEach(function(button) {
            searchProfileWidth += button.offsetWidth
          })
          const margin = Math.max(imageWidth, searchProfileWidth)
          const maxWidth = isScrolled ?
            document.querySelector(
              '.navigation-horizontal .v-toolbar__content',
            ).offsetWidth - (
              2 * margin
            ) - 40 - 16 :
            container.offsetWidth
          const maxWithBelow10 = document.querySelector(
            '.navigation-horizontal .v-toolbar__content',
          ).offsetWidth - imageWidth - searchProfileWidth - 40
          const buttons = document.querySelectorAll(
            '.container>.menu-row .v-btn .v-list-item__title',
          )
          buttons.forEach(button => {button.removeAttribute('style')})
          const subButtons = document.querySelectorAll(
            '.list.submenu-layer>.container>div .v-btn .v-list-item__title',
          )
          const baseFontSize = parseFloat(
            window.getComputedStyle(elem.querySelector('.v-btn .v-list-item__title'), null)
              .getPropertyValue('font-size'))
          const subFontSize = baseFontSize > 5 ? baseFontSize - 2 : baseFontSize
          buttons.forEach(button => {
            button.setAttribute('style', 'font-size: ' + baseFontSize + 'px;')
          })
          subButtons.forEach(button => {button.setAttribute('style', 'font-size: ' + subFontSize + 'px;')})
          container.classList.remove('container')
          for (let x = baseFontSize; x > 0; x--) {
            const subFontSize = x > 5 ? x - 2 : x
            buttons.forEach(button => {button.setAttribute('style', 'font-size: ' + x + 'px;')})
            subButtons.forEach(button => {button.setAttribute('style', 'font-size: ' + subFontSize + 'px;')})
            const menuWidth = elem.offsetWidth
            menu.classList.remove('logo-aligned')
            menu.style.left = null
            menu.style.width = null
            if (menuWidth > maxWidth) {
              if (x <= 11 && isScrolled) {
                menu.classList.add('logo-aligned')
                menu.style.left = `${imageWidth + 20 + 16}px`
                menu.style.width = `${maxWithBelow10}px`
                if (!(elem.offsetWidth > maxWithBelow10)) {
                  x = 0
                }
              }
            } else {
              x = 0
            }
          }
          container.classList.add('container')
          const buttonOffset = buttons[0].closest('a').getBoundingClientRect().bottom
          document.querySelectorAll('.submenu').forEach(subnav => {subnav.style.top = buttonOffset + 'px'})
          if (triggerScroll && typeof window.onScroll === 'function') {
            setTimeout(window.onScroll, 200)
          }
        })
      }, immediate ? 0 : 250)
    },
  },
}
</script>
